import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, AlertCircle, Mail, User as UserIcon, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Settings · RigBooks";
  }, []);

  const providers = user?.linked_providers?.length ? user.linked_providers : [user?.auth_provider || "email"];
  const canDelete = confirmText.trim().toUpperCase() === "DELETE";

  const deleteAccount = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api("/users/me", { method: "DELETE" });
      toast.success("Your account and all data were permanently deleted.");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.message || "Account deletion failed.");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Account */}
      <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
        <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">Account</h2>
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <UserIcon className="h-4 w-4 text-rig-accent" />
            <span className="text-white">{user?.name || "Driver"}</span>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-rig-accent" />
            <span className="text-white">{user?.email}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {providers.map((p) => (
              <span
                key={p}
                className="rounded-full bg-rig-surface2 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rig-dim"
              >
                {p} sign-in
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="flex items-center gap-3 rounded-2xl border border-rig-border bg-rig-surface p-5">
        <ShieldCheck className="h-5 w-5 shrink-0 text-rig-accent" />
        <p className="text-sm text-rig-dim">
          Your receipts, hauls and reports are stored in your private, audit-ready cloud archive. You own your data —
          export it anytime from <b className="text-white">Taxes &amp; IFTA</b>, or delete everything below.
        </p>
      </div>

      {/* Danger zone */}
      <div className="rounded-2xl border border-rig-error/50 bg-rig-error/5 p-6">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-rig-error" />
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-error">Danger Zone</h2>
        </div>
        <p className="mt-3 text-sm text-rig-dim">
          Permanently delete your account and <b className="text-white">all</b> associated data — receipts, hauls,
          trips, IFTA history and bookkeeper share links. This <b className="text-white">cannot be undone</b>.
        </p>
        <Label htmlFor="confirm-delete" className="mt-5 block font-display text-xs uppercase tracking-widest text-rig-dim">
          Type <span className="text-rig-error">DELETE</span> to confirm
        </Label>
        <Input
          id="confirm-delete"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          data-testid="delete-confirm-input"
          className="mt-1.5 h-11 max-w-xs border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
          autoComplete="off"
        />
        <Button
          onClick={deleteAccount}
          disabled={!canDelete || busy}
          data-testid="delete-account-btn"
          className="mt-4 h-12 bg-rig-error font-display text-sm font-bold uppercase tracking-widest text-white hover:bg-rig-error/90 disabled:opacity-40"
        >
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Trash2 className="mr-2 h-5 w-5" />}
          Delete my account
        </Button>
      </div>
    </div>
  );
}
